const get = require('lodash/get')
const { resolveWarehouseByLocationId } = require('../../order/tools/read/warehouse-code-to-virtual')

/**
 * Resolves a virtual warehouse origin ID to its real warehouse ID.
 * PSM tenants use funder-suffixed warehouse IDs (e.g. zone:nw:name:kano-pepfar)
 * that all map to the same physical warehouse (zone:nw:name:kano).
 *
 * For non-warehouse or non-PSM/EPN IDs, returns the original ID unchanged.
 */
function resolveRealOriginId (originId) {
  try {
    return resolveWarehouseByLocationId(originId)._id
  } catch (_) {
    return originId
  }
}

/**
 * Pure synchronous transform that shapes the enriched PG query result
 * into the same format as the old decorateRouteWithDetails.
 *
 * Expects routes from RouteViewPGDataAdapter.getDecoratedList() where
 * each shipment already has `origin`, `destination`, and `program` objects
 * pre-JOINed from data_location and data_program.
 *
 * @param {Array} routes - Routes from getDecoratedList
 * @returns {Array} Decorated routes matching the legacy format
 */
function transformDecoratedRoutes (routes) {
  if (!routes || routes.length === 0) {
    return []
  }

  return routes.map((route) => {
    const routeShipments = (route.shipments || []).map((shipment) => ({
      ...shipment,
      // Volume is deferred — set to 0 for list view.
      // Detailed volume is computed on the route detail endpoint.
      loadVolume: 0
    }))

    const routeShipmentsData = routeShipments.reduce(
      (acc, shipment) => {
        const lga = get(shipment, 'destination.lga', null)
        acc.lga.add(lga)
        const state = get(shipment, 'destination.state', null)
        acc.state.add(state)
        acc.destinations.add(shipment.destination_id)
        acc.origin.add(shipment.origin_id)
        acc.funders.add(shipment.funder_id)
        acc.programs.add(shipment.program_id)
        return acc
      },
      {
        lga: new Set(),
        state: new Set(),
        destinations: new Set(),
        origin: new Set(),
        funders: new Set(),
        programs: new Set()
      }
    )

    // Resolve origin: all shipments in a route should share the same origin.
    // PSM routes may have shipments with different virtual warehouse IDs
    // (e.g. zone:nw:name:kano-pepfar, zone:nw:name:kano-gf) that map to the
    // same physical warehouse. We resolve these before checking uniqueness.
    let origin = null
    const rawOriginIds = Array.from(routeShipmentsData.origin).filter(Boolean)
    const resolvedOriginIds = new Set(rawOriginIds.map(resolveRealOriginId))

    if (resolvedOriginIds.size === 1) {
      const realOriginId = Array.from(resolvedOriginIds)[0]
      // Prefer a shipment whose origin_id is the real (non-virtual) warehouse ID
      const shipmentWithOrigin = routeShipments.find(s => s.origin_id === realOriginId) ||
        routeShipments.find(s => s.origin_id && resolveRealOriginId(s.origin_id) === realOriginId)
      origin = shipmentWithOrigin ? shipmentWithOrigin.origin : null
    } else if (resolvedOriginIds.size > 1) {
      console.warn(`Route ${route.couchdb_id} has shipments from multiple origins: ${rawOriginIds.join(', ')}. Route will be excluded from results.`)
    }

    // Collect unique destination location objects
    const destinationsMap = new Map()
    routeShipments.forEach(s => {
      if (s.destination_id && s.destination && !destinationsMap.has(s.destination_id)) {
        destinationsMap.set(s.destination_id, s.destination)
      }
    })

    // Collect unique program objects
    const programsMap = new Map()
    routeShipments.forEach(s => {
      if (s.program_id && s.program && !programsMap.has(s.program_id)) {
        programsMap.set(s.program_id, s.program)
      }
    })

    return {
      id: route.couchdb_id,
      name: route.name,
      lga: Array.from(routeShipmentsData.lga).filter(Boolean).join(', '),
      state: Array.from(routeShipmentsData.state).filter(Boolean).join(', '),
      origin,
      destinations: Array.from(destinationsMap.values()),
      stopsNo: destinationsMap.size,
      load: 0, // Volume deferred to detail view
      createdAt: route.created_at,
      startDate: route.start_date,
      endDate: route.end_date,
      approvalDate: route.approval_date || null,
      estimatedDeliveryDate: route.estimated_delivery_date || null,
      estimatedPickupDate: route.estimated_pickup_date || null,
      funders: Array.from(routeShipmentsData.funders).filter(Boolean).map(fid => ({ _id: fid })),
      programs: Array.from(programsMap.values()),
      status: route.status,
      shipments: routeShipments,
      carrier: get(route, 'carrier', undefined),
      accepted: route.accepted
    }
  })
    .filter(route => {
      if (!route.name) return false
      if (route.shipments.length === 0) return true
      return !!route.origin
    })
}

module.exports = { transformDecoratedRoutes }
